import React, { useState, useEffect, useRef } from 'react';
import { labConfig } from '../config/tourConfig';

const LabSelector = ({ currentLab, onLabChange }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const tabsRef = useRef(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);

    // Порядок лабораторій
    const labOrder = ["318", "300-01", "300-02", "299-05", "299-06", "126", "120", "08", "06-1", "06-2", "05", "04"];
    const orderedLabs = labOrder.filter(labId => labConfig[labId]);

    // Оновлення видимості стрілок
    const updateArrowsVisibility = () => {
        if (!tabsRef.current) return;

        const { scrollLeft, scrollWidth, clientWidth } = tabsRef.current;
        setShowLeftArrow(scrollLeft > 0);
        setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
    };

    useEffect(() => {
        updateArrowsVisibility();
        window.addEventListener('resize', updateArrowsVisibility);
        return () => window.removeEventListener('resize', updateArrowsVisibility);
    }, []);

    // Прокрутка табів
    const scrollTabs = (direction) => {
        if (!tabsRef.current) return;

        const scrollAmount = 200;
        const currentScroll = tabsRef.current.scrollLeft;
        const targetScroll = direction === 'left'
            ? currentScroll - scrollAmount
            : currentScroll + scrollAmount;

        tabsRef.current.scrollTo({
            left: Math.max(0, targetScroll),
            behavior: 'smooth'
        });
    };

    // Прокрутка до активного табу
    const scrollToActiveTab = (labId) => {
        if (!tabsRef.current) return;

        const activeTab = tabsRef.current.querySelector(`[data-lab="${labId}"]`);
        if (!activeTab) return;

        const containerRect = tabsRef.current.getBoundingClientRect();
        const tabRect = activeTab.getBoundingClientRect();

        const isTabVisible = (
            tabRect.left >= containerRect.left &&
            tabRect.right <= containerRect.right
        );

        if (!isTabVisible) {
            const scrollLeft = activeTab.offsetLeft - (tabsRef.current.clientWidth / 2) + (activeTab.clientWidth / 2);
            tabsRef.current.scrollTo({
                left: Math.max(0, scrollLeft),
                behavior: 'smooth'
            });
        }
    };

    useEffect(() => {
        scrollToActiveTab(currentLab);
    }, [currentLab]);

    const handleTabClick = (labId) => {
        onLabChange(labId);
    };

    const handleDropdownToggle = () => {
        setShowDropdown(!showDropdown);
    };

    const handleDropdownItemClick = (labId) => {
        onLabChange(labId);
        setShowDropdown(false);
    };

    // Закриття dropdown при кліку поза ним
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.lab-dropdown')) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const currentLabName = labConfig[currentLab]?.name || 'Оберіть лабораторію';

    return (
        <div className="lab-selector">
            {/* Десктоп меню - табки */}
            <div className="lab-tabs-wrapper desktop-menu">
                <button
                    className={`scroll-arrow scroll-left ${showLeftArrow ? '' : 'hidden'}`}
                    onClick={() => scrollTabs('left')}
                    title="Прокрутити ліворуч"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M15.41 7.41L14 6l-6 6l6 6l1.41-1.41L10.83 12z" />
                    </svg>
                </button>

                <div
                    className="lab-tabs"
                    ref={tabsRef}
                    onScroll={updateArrowsVisibility}
                >
                    {orderedLabs.map(labId => (
                        <button
                            key={labId}
                            className={`lab-tab ${currentLab === labId ? 'active' : ''}`}
                            data-lab={labId}
                            onClick={() => handleTabClick(labId)}
                        >
                            <span className="tab-text">{labConfig[labId].name}</span>
                        </button>
                    ))}
                </div>

                <button
                    className={`scroll-arrow scroll-right ${showRightArrow ? '' : 'hidden'}`}
                    onClick={() => scrollTabs('right')}
                    title="Прокрутити праворуч"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M10 6L8.59 7.41L13.17 12l-4.58 4.59L10 18l6-6z" />
                    </svg>
                </button>
            </div>

            {/* Мобільне dropdown меню */}
            <div className="lab-dropdown mobile-menu">
                <button
                    className={`dropdown-toggle ${showDropdown ? 'active' : ''}`}
                    onClick={handleDropdownToggle}
                >
                    <span className="current-lab-text">{currentLabName}</span>
                    <svg
                        className="dropdown-arrow"
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                    >
                        <path fill="currentColor" d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6l-6-6l1.41-1.41z" />
                    </svg>
                </button>

                <div className={`dropdown-menu ${showDropdown ? 'show' : ''}`}>
                    {orderedLabs.map(labId => (
                        <button
                            key={labId}
                            className={`dropdown-item ${currentLab === labId ? 'active' : ''}`}
                            onClick={() => handleDropdownItemClick(labId)}
                        >
                            <span className="tab-text">{labConfig[labId].name}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default LabSelector;
